import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Input,
  Select,
  DatePicker,
  Table,
  Tag,
  Tooltip,
  Empty,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import StatusEnum from "features/Enums/StatusEnum";
import { GetEstimatePdfApi } from "features/Clients/ClientsApi";
import { getQuotesListByCompanyIdAPI } from "./QuotesApi";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { FilterFilled } from "@ant-design/icons";
import { getQuoteTypeAPI } from "CommonComponents/Lookup/LookupAPI";

function QuotesList() {
  const { Column } = Table;
  const dispatch = useDispatch();
  const intl = useIntl();
  const { Search } = Input;
  const navigate = useNavigate();
  const { RangePicker } = DatePicker;
  const companyId = localStorage.getItem("CompanyId");
  const quotes = useSelector((state) => state.quotes.quotesList);
  const employeeList = useSelector((state) => state.lookup.employeesList);
  const totalPages = useSelector((state) => state.quotes.totalPages);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dateRange, setDateRange] = useState(null);
  const [searchKey, setSearchKey] = useState(Date.now());
  const quoteTypes = useSelector((state) => state.lookup.quoteTypes);
  const [selectedQuoteTypeIds, setSelectedQuoteTypeIds] = useState([]);
  const [selectedStatusIds, setSelectedStatusIds] = useState([]);
  const selectedLanguage = localStorage.getItem("selectedlanguage");

  const handlePrint = async (estimate) => {
    try {
      await Promise.all([
        dispatch(GetEstimatePdfApi(estimate.id)).then((response) => {
          if (response) {
            const base64 = response?.value?.pdfBase64;
            const fileName = `${estimate.estimateNo}.pdf`;
            const pdfDataUrl = `data:application/pdf;base64,${base64}`;

            const pdfWindow = window.open("", "_blank");

            if (pdfWindow) {
              pdfWindow.document.write(`
                <html>
                    <head><title>${fileName}</title></head>
                    <body style="margin:0">
                      <iframe width="100%" height="100%" src="${pdfDataUrl}" frameborder="0"></iframe>
                    </body>
                </html>
              `);
              pdfWindow.document.close();
            }
          }
        }),
      ]);
    } catch (e) {
      console.error("Error previewing PDF:", e);
    }
  };
  const statusMap = {
    1: { text: "Draft", textFr: "Brouillon", color: "gold" },
    2: { text: "Submitted", textFr: "Soumis", color: "blue" },
    3: { text: "Accepted", textFr: "Accepté", color: "green" },
    4: { text: "Rejected", textFr: "Rejeté", color: "red" },
  };
  const getEmployeeNameById = (salesPersonId) => {
    const employee = employeeList?.find((item) => item?.id == salesPersonId);
    return employee ? `${employee.firstName} ${employee.lastName}` : "N/A";
  };
  const handleEditClick = (record) => {
    if (record?.partnerId && record?.clientId) {
      navigate(
        `/${companyId}/${record?.partnerId}/partner_client_details/${record?.id}/editestimate`
      );
    } else {
      navigate(
        `/${companyId}/${record?.clientId}/client_details/${record?.id}/editestimate`
      );
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const pagination = {
    pageNo: currentPage,
    pageSize: 10,
    searchQuery: searchTerm,
    startDate: dateRange?.[0] ? dayjs(dateRange[0]).format("YYYY-MM-DD") : null,
    endDate: dateRange?.[1] ? dayjs(dateRange[1]).format("YYYY-MM-DD") : null,
    quoteTypeIds: selectedQuoteTypeIds.length > 0 ? selectedQuoteTypeIds : null,
    statusIds: selectedStatusIds.length > 0 ? selectedStatusIds : null,
  };

  useEffect(() => {
    dispatch(getQuotesListByCompanyIdAPI(pagination));
    dispatch(getQuoteTypeAPI());
  }, [
    currentPage,
    searchTerm,
    dateRange,
    selectedQuoteTypeIds,
    selectedStatusIds,
  ]);
  return (
    <>
      <Row align="bottom" gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={4} xl={6}>
          <RangePicker
            style={{ width: "100%" }}
            className="mobile-range-picker"
            popupClassName="mobile-range-popup"
            getPopupContainer={(trigger) => trigger.parentElement}
            value={dateRange}
            placeholder={[
              intl.formatMessage({
                id: "Quote.StartDate",
              }),
              intl.formatMessage({
                id: "Quote.EndDate",
              }),
            ]}
            onChange={(dates) => {
              setDateRange(dates);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={8} xl={8}>
          <Search
            key={searchKey}
            allowClear
            onSearch={handleSearch}
            defaultValue={searchTerm}
            placeholder={intl.formatMessage({
              id: "Quote.search.SearchbyquoteIdclientname",
            })}
          />
        </Col>
        <Col xs={24} sm={12} md={8} lg={4} xl={4}>
          <Button
            onClick={() => {
              setSearchTerm("");
              setDateRange(null);
              setSearchKey(Date.now());
              setCurrentPage(1);
            }}
          >
            {<FormattedMessage id="Lead.ClearFilters" />}
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={quotes}
        scroll={{
          x: 900,
        }}
        pagination={{
          current: currentPage,
          total: totalPages * 10,
          showSizeChanger: false,
          onChange: handlePageChange,
        }}
        className="mt-10"
        bordered
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={intl.formatMessage({
                id: "NoData",
              })}
            />
          ),
        }}
      >
        <Column
          title={<FormattedMessage id="ClientDetails.QuatationNo" />}
          dataIndex="estimateNo"
          key="quateno"
          render={(_, record) => (
            <Link
              to={
                record?.partnerId
                  ? `/${companyId}/${record?.partnerId}/partner_client_details/${record?.id}/viewquote`
                  : `/${companyId}/${record?.clientId}/client_details/${record?.id}/viewquote`
              }
            >
              {record?.estimateNo}
            </Link>
          )}
        />

        <Column
          title={
            <span>
              <FormattedMessage id="Clients.ClientName" /> /{" "}
              <FormattedMessage id="Dashboard.Clients.PartnerName" />
            </span>
          }
          dataIndex="clientOrPartnerName"
          key="clientOrPartnerName"
        />

        {/* <Column
            title={<FormattedMessage id="ClientDetails.Date" />}
            dataIndex="dateTime"
            key="date"
          /> */}
        <Column
          title={<FormattedMessage id="ClientDetails.QuoteType" />}
          dataIndex="quoteTypeName"
          key="quoteTypeName"
          filterDropdown={({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }) => (
            <div style={{ padding: 8 }}>
              {quoteTypes?.map((type) => {
                const label =
                  selectedLanguage === "fr-FR" ? type.nameFr : type.nameEn;
                const checked = selectedQuoteTypeIds.includes(type.id);
                return (
                  <div key={type.id} style={{ marginBottom: 4 }}>
                    <label style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const newSelected = checked
                            ? selectedQuoteTypeIds.filter(
                                (id) => id !== type.id
                              )
                            : [...selectedQuoteTypeIds, type.id];
                          setSelectedQuoteTypeIds(newSelected);
                          setCurrentPage(1);
                        }}
                        style={{ marginRight: 8 }}
                      />
                      {label}
                    </label>
                  </div>
                );
              })}
              <Button
                onClick={() => {
                  setSelectedQuoteTypeIds([]);
                  setCurrentPage(1);
                }}
                size="small"
                style={{ marginTop: 8, width: "100%" }}
              >
                {intl.formatMessage({ id: "Common.Reset" })}
              </Button>
            </div>
          )}
          filterIcon={(filtered) => (
            <FilterFilled
              style={{ color: filtered ? "#1890ff" : "white", fontSize: 15 }}
            />
          )}
          onFilter={(value, record) =>
            Array.isArray(value)
              ? value.includes(
                  selectedLanguage === "fr-FR"
                    ? record.quoteTypeNameFr || record.quoteTypeName
                    : record.quoteTypeName
                )
              : (selectedLanguage === "fr-FR"
                  ? record.quoteTypeNameFr || record.quoteTypeName
                  : record.quoteTypeName) === value
          }
          render={(_, record) =>
            selectedLanguage === "fr-FR"
              ? record.quoteTypeNameFr || record.quoteTypeName
              : record.quoteTypeName
          }
        />

        <Column
          title={<FormattedMessage id="ClientDetails.ExpiryDate" />}
          dataIndex="expiryDate"
          key="expirydate"
        />

        <Column
          title={<FormattedMessage id="ClientDetails.Amount" />}
          dataIndex="grandTotal"
          key="amount"
        />
        <Column
          title={<FormattedMessage id="Quote.SalesPerson" />}
          dataIndex="salesPersonId"
          key="salesPersonId"
          render={(text, record) => getEmployeeNameById(record.salesPersonId)}
        />

        <Column
          title={<FormattedMessage id="ClientDetails.Status" />}
          dataIndex="status"
          key="status"
          filterDropdown={({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
          }) => (
            <div style={{ padding: 8 }}>
              {Object.entries(statusMap).map(([id, statusInfo]) => {
                const statusId = Number(id);
                const checked = selectedStatusIds.includes(statusId);
                const label =
                  selectedLanguage === "fr-FR"
                    ? statusInfo.textFr
                    : statusInfo.text;
                return (
                  <div key={statusId} style={{ marginBottom: 4 }}>
                    <label style={{ cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const newSelected = checked
                            ? selectedStatusIds.filter(
                                (sid) => sid !== statusId
                              )
                            : [...selectedStatusIds, statusId];
                          setSelectedStatusIds(newSelected);
                          setCurrentPage(1);
                        }}
                        style={{ marginRight: 8 }}
                      />
                      {label}
                    </label>
                  </div>
                );
              })}
              <Button
                onClick={() => {
                  setSelectedStatusIds([]);
                  setCurrentPage(1);
                }}
                size="small"
                style={{ marginTop: 8, width: "100%" }}
              >
                {intl.formatMessage({ id: "Common.Reset" })}
              </Button>
            </div>
          )}
          filterIcon={(filtered) => (
            <FilterFilled
              style={{ color: filtered ? "#1890ff" : "white", fontSize: 15 }}
            />
          )}
          onFilter={(value, record) =>
            Array.isArray(value)
              ? value.includes(record.estimateStatusId)
              : record.estimateStatusId === value
          }
          render={(text, record) => {
            const isDraftOrSubmitted =
              record.estimateStatusId === StatusEnum.Draft?.id ||
              record.estimateStatusId === StatusEnum.Submitted?.id;

            const today = new Date();
            const expiry = new Date(record.expiryDate);
            const isExpired =
              isDraftOrSubmitted &&
              new Date(expiry.toDateString()) <
                new Date(new Date().toDateString());

            if (isExpired) {
              const label = selectedLanguage === "fr-FR" ? "Expiré" : "Expired";
              return <Tag color="red">{label}</Tag>;
            }

            const statusInfo = statusMap[record.estimateStatusId] || {
              text: "Unknown",
              textFr: "Inconnu",
              color: "default",
            };
            const label =
              selectedLanguage === "fr-FR"
                ? statusInfo.textFr
                : statusInfo.text;
            return <Tag color={statusInfo.color}>{label}</Tag>;
          }}
        />
        <Column
          title={<FormattedMessage id="ClientDetails.Action" />}
          key="action"
          render={(_, record) => {
            const isDraftOrSubmitted =
              record.estimateStatusId === StatusEnum.Draft?.id ||
              record.estimateStatusId === StatusEnum.Submitted?.id;

            const today = new Date();
            const expiry = new Date(record.expiryDate);
            const isExpired =
              isDraftOrSubmitted &&
              new Date(expiry.toDateString()) <
                new Date(new Date().toDateString());

            const disableEdit =
              record.estimateStatusId === StatusEnum.Accepted?.id ||
              record.estimateStatusId === StatusEnum.Rejected?.id ||
              isExpired;

            return (
              <Space size="middle">
                <Tooltip
                  title={
                    disableEdit ? (
                      <FormattedMessage id="Quote.QuoteCannotBeEdited" />
                    ) : (
                      <FormattedMessage id="Common.Edit" />
                    )
                  }
                >
                  <span>
                    <Button
                      size="small"
                      disabled={disableEdit}
                      onClick={() => !disableEdit && handleEditClick(record)}
                    >
                      <EditOutlined />
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title={<FormattedMessage id="ClientDetails.Preview" />}
                >
                  <Button onClick={() => handlePrint(record)} size="small">
                    <FilePdfOutlined />
                  </Button>
                </Tooltip>
              </Space>
            );
          }}
        />
      </Table>
    </>
  );
}

export default QuotesList;
